import { useState, useRef, useCallback, useEffect } from 'react';
import VoiceBot from '@qnova-ac/voicebot-web-sdk';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { Phone, PhoneOff, AlertCircle, Mic } from 'lucide-react';
import voiceCallService from '@/services/voiceCallService';
import * as Sentry from '@sentry/react';

interface VoiceCallConfig {
  onCallStart?: () => void;
  onCallEnd?: () => void;
  onError?: (error: any) => void;
}

interface VoiceCallState {
  isConnecting: boolean;
  isConnected: boolean;
  isMuted: boolean;
  volumeLevel: number;
  isAssistantSpeaking: boolean;
}

export function useVoiceCall(config: VoiceCallConfig = {}) {
  const { error: showError, info, success } = useEnhancedToast();
  const voiceBotRef = useRef<VoiceBot | null>(null);
  const listenersSetupRef = useRef(false);
  const [state, setState] = useState<VoiceCallState>({
    isConnecting: false,
    isConnected: false,
    isMuted: false,
    volumeLevel: 0,
    isAssistantSpeaking: false,
  });

  // Initialize VoiceBot instance
  useEffect(() => {
    const initializeVoiceBot = async () => {
      try {
        // Get the VoiceBot instance with JWT authentication
        voiceBotRef.current = await voiceCallService.getVoiceBotInstance();
        
        // Only set up event listeners once per component mount
        if (!listenersSetupRef.current) {
          listenersSetupRef.current = true;
          
          // Remove any existing listeners first
          voiceBotRef.current.removeAllListeners();
          
          // Set up event listeners
          voiceBotRef.current.on('call-start', () => {
            setState(prev => ({ ...prev, isConnecting: false, isConnected: true }));
            success({
              icon: Phone,
              title: "Connected",
              description: "You're now connected to your assistant",
              duration: 3000
            });
            config.onCallStart?.();
          });

          voiceBotRef.current.on('call-end', () => {
            setState(prev => ({ 
              ...prev, 
              isConnecting: false, 
              isConnected: false,
              isMuted: false,
              volumeLevel: 0,
              isAssistantSpeaking: false 
            }));
            info({
              icon: PhoneOff,
              title: "Call Ended",
              description: "Your conversation has ended",
              duration: 3000
            });
            config.onCallEnd?.();
          });

          voiceBotRef.current.on('speech-start', () => {
            setState(prev => ({ ...prev, isAssistantSpeaking: true }));
          });

          voiceBotRef.current.on('speech-end', () => {
            setState(prev => ({ ...prev, isAssistantSpeaking: false }));
          });

          voiceBotRef.current.on('volume-level', (volume: number) => {
            setState(prev => ({ ...prev, volumeLevel: volume }));
          });

          voiceBotRef.current.on('error', (e: any) => {
            // Log to Sentry with context
            Sentry.captureException(e, {
              contexts: {
                voicecall: {
                  event_type: 'voicebot_error',
                  is_connected: state.isConnected,
                  is_connecting: state.isConnecting,
                },
              },
              tags: {
                feature: 'voice_call',
                error_source: 'voicebot_sdk',
              },
              level: 'error',
            });
            
            showError({
              icon: AlertCircle,
              title: "Connection Error",
              description: "An error occurred during the call. Please try again.",
              duration: 4000
            });
            config.onError?.(e);
            setState(prev => ({ 
              ...prev, 
              isConnecting: false, 
              isConnected: false 
            }));
          });
        }
      } catch (error) {
        // Log to Sentry
        Sentry.captureException(error, {
          contexts: {
            voicecall: {
              event_type: 'voicebot_init_failed',
              error_phase: 'initialization',
            },
          },
          tags: {
            feature: 'voice_call',
            error_source: 'voicebot_init',
          },
          level: 'error',
        });
        
        console.error('Failed to initialize VoiceBot:', error);
        showError({
          icon: AlertCircle,
          title: "Authentication Error",
          description: "Please ensure you're logged in and try again.",
          duration: 4000
        });
      }
    };

    initializeVoiceBot();

    // Cleanup function
    return () => {
      // Remove listeners when component unmounts
      if (voiceBotRef.current && listenersSetupRef.current) {
        voiceBotRef.current.removeAllListeners();
        listenersSetupRef.current = false;
      }
    };
  }, []); // Remove publicKey dependency

  const startCall = useCallback(async (assistantId: string) => {
    if (!assistantId) {
      showError({
        icon: AlertCircle,
        title: "Cannot Start Call",
        description: "Assistant ID is required",
        duration: 4000
      });
      return;
    }

    // Ensure we have a fresh VoiceBot instance with valid token
    try {
      voiceBotRef.current = await voiceCallService.getVoiceBotInstance();
    } catch (error) {
      showError({
        icon: AlertCircle,
        title: "Authentication Error",
        description: "Please ensure you're logged in and try again.",
        duration: 4000
      });
      return;
    }

    if (!voiceBotRef.current) {
      showError({
        icon: AlertCircle,
        title: "Cannot Start Call",
        description: "VoiceBot service is not available",
        duration: 4000
      });
      return;
    }

    setState(prev => ({ ...prev, isConnecting: true }));

    try {
      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      info({
        icon: Mic,
        title: "Connecting...",
        description: "Setting up your conversation",
        duration: 2000
      });

      await voiceBotRef.current.start(assistantId);
    } catch (err: any) {
      setState(prev => ({ ...prev, isConnecting: false }));
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        showError({
          icon: AlertCircle,
          title: "Microphone Access Required",
          description: "Please allow microphone access to talk with your assistant",
          duration: 5000
        });
      } else {
        // Log the actual error for debugging but don't expose it to users
        // console.error('Failed to start call:', err);
        
        showError({
          icon: AlertCircle,
          title: "Connection Failed",
          description: "Failed to start the conversation. Please check your connection and try again.",
          duration: 4000
        });
      }
    }
  }, [showError, info]);

  const endCall = useCallback(() => {
    if (voiceBotRef.current && state.isConnected) {
      voiceBotRef.current.stop();
    }
  }, [state.isConnected]);

  const toggleMute = useCallback(() => {
    if (voiceBotRef.current && state.isConnected) {
      const newMutedState = !state.isMuted;
      voiceBotRef.current.setMuted(newMutedState);
      setState(prev => ({ ...prev, isMuted: newMutedState }));
    }
  }, [state.isConnected, state.isMuted]);

  const sendMessage = useCallback((content: string, role: 'system' | 'user' = 'system') => {
    if (voiceBotRef.current && state.isConnected) {
      voiceBotRef.current.send({
        type: 'add-message',
        message: { role, content }
      });
    }
  }, [state.isConnected]);

  return {
    ...state,
    startCall,
    endCall,
    toggleMute,
    sendMessage,
  };
} 